import "@/assets/styles/table.order.css";

import BasicTable from "@/components/shared/BasicTable";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import classNames from "classnames";
import { useCallback, useMemo, useRef } from "react";
import { Button, Popover } from "antd";
import { useGlobalContext } from "@/hooks/context";
import {
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import {
  IntegrationOrderSearchParams,
  IntegrationOrderStatus,
  IntegrationOrderType,
  IntegrationOrderVo,
} from "@/types/integration";
import { useIntegrationOrders } from "./helper";
import AddressInfo from "@/components/shared/AddressInfo";
import { EcovacsOrderRequestRaw } from "./raw/EcovacsOrder";
import { BaseusOrderRequestRaw } from "./raw/BaseusOrder";
import { InstaOrderRequestRaw } from "./raw/InstaOrder";
import Indicator from "@/components/shared/Indicator";
import { DialogRef } from "@/types";
import { DeleteOrderButton } from "./OrderDelete";
import OrderModifyCube from "./OrderModifyCube";
import { ReportDialog } from "../shipment/ReportDialog";

interface OrdersTableProps {
  className?: string;
  cols: ColDef<IntegrationOrderVo>[];
  query: IntegrationOrderSearchParams;
  onQueryChange: (query: IntegrationOrderSearchParams) => void;
}

export default function IntegrationOrdersTable(props: OrdersTableProps) {
  const { cols, className, query, onQueryChange } = props;
  const { data: orders, refetch, isFetching } = useIntegrationOrders(query);
  const { hintPrompt } = useGlobalContext();

  const deleteOrderButtonRef = useRef<DialogRef>(null);
  const orderModifyCubeRef = useRef<DialogRef>(null);
  const reportDialogRef = useRef<DialogRef>(null);

  const onQueryParamsChange = useCallback(
    (params: Partial<IntegrationOrderSearchParams>) => {
      onQueryChange(params);
    },
    [onQueryChange],
  );

  const actionColumn = useMemo(() => {
    return {
      headerName: "Action",
      minWidth: 172,
      maxWidth: 172,
      resizable: false,
      pinned: "right",
      suppressMenu: true,
      cellRenderer: ({
        data,
      }: ICellRendererParams<IntegrationOrderVo, string>) => {
        return (
          <div className="flex gap-1">
            <Button
              icon={<TruckOutlined />}
              disabled={
                data == null ||
                data.type !== IntegrationOrderType.Business ||
                data.customerName !== "INSTA" ||
                data.status === IntegrationOrderStatus.Submitted
              }
              onClick={() => {
                reportDialogRef.current?.showModal({
                  riiOrderNo: `OTRII${data!.cubeDeliveryNo!}`,
                });
              }}
            />
            <Button
              icon={<EditOutlined />}
              disabled={
                data == null || data.status !== IntegrationOrderStatus.Created
              }
              onClick={() => {
                orderModifyCubeRef.current?.showModal({
                  orderId: data?.id ?? 0,
                  type: data?.type,
                  customerOrderNo: data?.customerNumber,
                });
              }}
            />
            <Popover
              placement="left"
              content={data && <AddressInfo address={data.address} />}
              trigger="click"
            >
              <Button
                title="View Address"
                icon={<SendOutlined />}
                disabled={data == null || data.address == null}
              />
            </Popover>
            <Button
              icon={<DeleteOutlined />}
              danger
              disabled={
                data == null || data.status === IntegrationOrderStatus.Deleted
              }
              onClick={() =>
                data &&
                deleteOrderButtonRef.current?.showModal({
                  orderId: data.id,
                  cubeOrderId: data.cubeOrderId,
                  type: data.type,
                  customerOrderNo: data.customerNumber,
                  deleteCb: refetch,
                })
              }
            />
          </div>
        );
      },
    } as ColDef<IntegrationOrderVo>;
  }, [refetch]);

  const columns = useMemo(() => [...cols, actionColumn], [cols, actionColumn]);

  const rowDetailRender = useCallback((data?: IntegrationOrderVo) => {
    if (!data) return null;
    return (
      <div className="pb-2 pt-1 px-3 bg-gray-100 h-full">
        <RequestRawBasedOnCustomer
          customerName={data!.customerName}
          rawData={data!.extra}
        />
      </div>
    );
  }, []);

  return (
    <>
      <BasicTable
        className={classNames("order-table", className)}
        columns={columns}
        page={orders}
        loading={isFetching}
        getRowId={(row) => `${row.id}`}
        enableRowDetail={true}
        enablePagination={true}
        rowHeight={52}
        onRefreshData={refetch}
        onSortChange={(sorts, dirs) => {
          onQueryParamsChange({ sorts, dirs });
        }}
        onPaginationChange={(page, size) => {
          onQueryParamsChange({ size, page });
        }}
        detailRowHeight={240}
        rowDetailRender={rowDetailRender}
      />
      <DeleteOrderButton ref={deleteOrderButtonRef} />
      <OrderModifyCube ref={orderModifyCubeRef} updateCb={refetch} />
      <ReportDialog
        ref={reportDialogRef}
        onSuccess={() =>
          hintPrompt("success", "Shipment report sent successfully")
        }
      />
    </>
  );
}

interface RequestRawBasedOnCustomerProps {
  customerName: string;
  rawData: Record<string, unknown>;
}

function RequestRawBasedOnCustomer(props: RequestRawBasedOnCustomerProps) {
  const { customerName, rawData } = props;

  return (
    <div className="overflow-y-auto h-full">
      <Indicator label="Original Order Data" />
      {customerName === "ECOVACS" && (
        <EcovacsOrderRequestRaw
          rawData={rawData}
          className="bg-white !rounded-[8px]"
          labelStyle={{ width: "16%" }}
          contentStyle={{ width: "36%" }}
        />
      )}
      {customerName === "BASEUS" && (
        <BaseusOrderRequestRaw
          rawData={rawData}
          className="bg-white !rounded-[8px]"
          labelStyle={{ width: "16%" }}
          contentStyle={{ width: "36%" }}
        />
      )}
      {customerName === "INSTA" && (
        <InstaOrderRequestRaw
          rawData={rawData}
          className="bg-white !rounded-[8px]"
          labelStyle={{ width: "16%" }}
          contentStyle={{ width: "36%" }}
        />
      )}
    </div>
  );
}
